/**
 * Analytics Tracking Hook
 *
 * Phase 16: Enterprise Audit Trail System
 * Automatically tracks page views and user activity for analytics.
 */

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuthContext } from './AuthContext'
import { useStudyStore } from '@/stores/studyStore'
import { trackAnalyticsEvent } from '@/lib/auditApi'

// Generate or retrieve session ID for analytics
function getSessionId(): string {
  let sessionId = sessionStorage.getItem('analytics_session_id')
  if (!sessionId) {
    sessionId = crypto.randomUUID()
    sessionStorage.setItem('analytics_session_id', sessionId)
  }
  return sessionId
}

/**
 * Hook to automatically track page views when routes change.
 * Should be called once at the app root level.
 */
export function useAnalyticsTracking() {
  const location = useLocation()
  const { isAuthenticated, user } = useAuthContext()
  const { currentStudy } = useStudyStore()
  const lastTrackedPath = useRef<string | null>(null)
  const isTrackingRef = useRef(false)

  useEffect(() => {
    // Only track for authenticated users
    if (!isAuthenticated || !user) {
      return
    }

    // Skip if we already tracked this path (avoid double tracking)
    const currentPath = location.pathname + location.search
    if (lastTrackedPath.current === currentPath) {
      return
    }

    // Prevent concurrent tracking calls
    if (isTrackingRef.current) {
      return
    }

    // Skip tracking for certain paths
    const skipPaths = ['/login', '/logout', '/auth']
    if (skipPaths.some(path => location.pathname.startsWith(path))) {
      return
    }

    const trackPageView = async () => {
      isTrackingRef.current = true
      try {
        await trackAnalyticsEvent({
          event_type: 'page_view',
          page_path: location.pathname,
          study_id: currentStudy?.studyId,
          session_id: getSessionId(),
          event_metadata: {
            search: location.search || undefined,
            referrer: document.referrer || undefined,
          },
        })
        lastTrackedPath.current = currentPath
      } catch (error) {
        // Silently fail - analytics should not block user experience
        console.debug('Analytics tracking failed:', error)
      } finally {
        isTrackingRef.current = false
      }
    }

    // Small delay to ensure auth state is stable
    const timeoutId = setTimeout(trackPageView, 100)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [location.pathname, location.search, isAuthenticated, user, currentStudy?.studyId])
}

/**
 * Track a custom analytics event (for manual tracking).
 */
export async function trackCustomEvent(
  eventType: string,
  metadata?: Record<string, unknown>,
  studyId?: string,
  moduleKey?: string
): Promise<void> {
  try {
    await trackAnalyticsEvent({
      event_type: eventType,
      page_path: window.location.pathname,
      study_id: studyId,
      module_key: moduleKey,
      session_id: getSessionId(),
      event_metadata: metadata,
    })
  } catch (error) {
    console.debug('Custom event tracking failed:', error)
  }
}

// Common event types for consistency
export const AnalyticsEventTypes = {
  PAGE_VIEW: 'page_view',
  MODULE_VIEW: 'module_view',
  DATA_EXPORT: 'data_export',
  DATA_FILTER: 'data_filter',
  ISSUE_CREATE: 'issue_create',
  SEARCH: 'search',
  BUTTON_CLICK: 'button_click',
} as const
